// US-SETUP-02 -- Add a child to my family.
//
// Reachable from the guardian's home screen via the "Add child" button.
// Owns its own validation + submission state.

import { type AppError, type ChildCreateResult, childCreate } from '@chorepop/core/client';
import { type Component, For, Show, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';

type Props = {
  sessionId: string;
  familyCurrencyCode?: 'GBP' | 'USD';
  onCreated: (child: ChildCreateResult) => void;
  onCancel: () => void;
};

const GUARDIAN_TYPES = [
  { code: 'parent', label: 'Parent' },
  { code: 'step_parent', label: 'Step-parent' },
  { code: 'grandparent', label: 'Grandparent' },
  { code: 'aunt_uncle', label: 'Aunt / Uncle' },
  { code: 'legal_guardian', label: 'Legal guardian' },
  { code: 'foster_parent', label: 'Foster parent' },
  { code: 'sibling', label: 'Adult sibling' },
  { code: 'other', label: 'Other' },
];

export const AddChildScreen: Component<Props> = (props) => {
  const [displayName, setDisplayName] = createSignal('');
  const [password, setPassword] = createSignal('');
  const [guardianType, setGuardianType] = createSignal('parent');
  const [submitting, setSubmitting] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal<string | null>(null);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setErrorMessage(null);

    const trimmedName = displayName().trim();
    if (trimmedName.length === 0) {
      setErrorMessage('Please enter a name for the child.');
      return;
    }
    if (password().length < 10) {
      setErrorMessage(`Password must be at least 10 characters (was ${password().length}).`);
      return;
    }

    setSubmitting(true);
    try {
      const result = await childCreate({
        sessionId: props.sessionId,
        displayName: trimmedName,
        password: password(),
        displayCurrencyCode: props.familyCurrencyCode ?? 'GBP',
        guardianTypeCode: guardianType(),
      });
      if (!result.ok) {
        setErrorMessage(humanReadableError(result.error));
        return;
      }
      setPassword('');
      setDisplayName('');
      props.onCreated(result.value);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ActionScreen
      eyebrow="Your family"
      title="Add a child"
      onBack={props.onCancel}
      footer={
        <button
          type="submit"
          form="add-child-form"
          class="primary action-screen-primary"
          disabled={submitting()}
        >
          {submitting() ? 'Adding…' : 'Add child'}
        </button>
      }
    >
      <p class="action-screen-intro">
        Create an account for a child in your family. They'll appear in the account-selection screen
        and can sign in with the password you set here. You can change their password later.
      </p>

      <form id="add-child-form" onSubmit={handleSubmit} novalidate class="action-form">
        <label class="field">
          <span class="field-label">Child's name</span>
          <input
            type="text"
            autocomplete="off"
            required
            autofocus
            disabled={submitting()}
            value={displayName()}
            onInput={(e) => {
              setDisplayName(e.currentTarget.value);
              setErrorMessage(null);
            }}
          />
        </label>

        <label class="field">
          <span class="field-label">Password</span>
          <input
            type="password"
            autocomplete="new-password"
            required
            disabled={submitting()}
            value={password()}
            onInput={(e) => {
              setPassword(e.currentTarget.value);
              setErrorMessage(null);
            }}
          />
          <span class="field-hint">At least 10 characters.</span>
        </label>

        <label class="field">
          <span class="field-label">Your relationship</span>
          <select
            disabled={submitting()}
            value={guardianType()}
            onChange={(e) => setGuardianType(e.currentTarget.value)}
          >
            <For each={GUARDIAN_TYPES}>{(g) => <option value={g.code}>{g.label}</option>}</For>
          </select>
        </label>

        <Show when={errorMessage()}>
          {(msg) => (
            <div class="form-error" role="alert">
              {msg()}
            </div>
          )}
        </Show>
      </form>
    </ActionScreen>
  );
};

function humanReadableError(error: AppError): string {
  switch (error.kind) {
    case 'validation':
      return error.message;
    case 'conflict':
      return error.message;
    case 'unauthorized':
      return 'Your session has expired. Please sign in again.';
    case 'network_required':
      return error.message;
    default:
      return `Something went wrong: ${error.message}`;
  }
}
